// The list row plus its two admin pills.
//
// On the `components/ui` primitives: every status/plan/role pill is `Badge`
// (the `planColors`/`statusColors` class maps in ./format are gone — the
// emerald/yellow spellings folded onto the green/amber roles). The two toggles
// stay real buttons because they are actions, so they wrap a Badge rather than
// being one.

import { useState } from 'react'
import type { ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { usersApi, type PlatformUser } from '../../api/client'
import { Badge, LinkButton, cx } from '../../components/ui'
import type { BadgeTone } from '../../components/ui'
import { capKey, extractApiError, formatDate, formatTimeAgo, planTone, statusTone, type Plan } from './format'
import { UserDetailPanel } from './UserDetailPanel'

/** The pill shape both toggles use: a button whose label is a Badge. */
function PillButton({ tone, onClick, disabled, title, ariaLabel, flagged, children }: {
  tone: BadgeTone
  onClick: () => void
  disabled?: boolean
  title: string
  ariaLabel: string
  /** Amber ring while the last attempt is unresolved. */
  flagged?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick() }}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
      className={cx(
        'rounded-full transition-opacity hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        flagged && 'ring-2 ring-amber-400',
      )}
    >
      <Badge tone={tone} className="uppercase tracking-wide">{children}</Badge>
    </button>
  )
}

export function MailToggle({ user }: { user: PlatformUser }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  // One pill, three states:
  //   green  ACTIVE  — paid sub + not blocked. Click → block.
  //   red    BLOCKED — admin blocked. Click → unblock.
  //   gray   —       — no paid sub. Click → block (preventive).
  // Click always flips mail_blocked; subscription status is read-only
  // (derived from mail_subscriptions). The single pill keeps the row
  // visually quiet while still encoding both bits of info via colour.
  const blockMut = useMutation({
    mutationFn: (blocked: boolean) =>
      usersApi.updateMailAccess(user.id, { mail_blocked: blocked }),
    onSuccess: () => {
      setErrorMsg(null)
      qc.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (e: unknown) => {
      setErrorMsg(extractApiError(e) ?? t('users.mailToggleFailed'))
    },
  })
  const blocked = user.mail_blocked
  const paidSub = user.mail_has_paid_sub

  const label = blocked
    ? t('users.mailPillBlocked', { defaultValue: 'Blocked' })
    : paidSub
    ? t('users.mailPillActive', { defaultValue: 'Active' })
    : t('users.mailPillNone', { defaultValue: '-' })
  const tone: BadgeTone = blocked ? 'danger' : paidSub ? 'success' : 'neutral'
  const title = blocked
    ? t('users.mailPillBlockedHint', { defaultValue: 'Mail blocked by admin. Click to unblock.' })
    : paidSub
    ? t('users.mailPillActiveHint', { defaultValue: 'Mail subscription active. Click to block.' })
    : t('users.mailPillNoneHint', { defaultValue: 'No mail subscription. Click to pre-emptively block.' })

  return (
    <span className="inline-flex items-center gap-2">
      <PillButton
        tone={tone}
        title={title}
        flagged={Boolean(errorMsg)}
        disabled={blockMut.isPending}
        ariaLabel={t('users.mailBlockToggleAria', { defaultValue: 'Toggle mail block' })}
        onClick={() => { setErrorMsg(null); blockMut.mutate(!blocked) }}
      >
        {label}
      </PillButton>
      {errorMsg && (
        <span className="text-[10px] text-amber-700 max-w-[10rem] truncate" title={errorMsg}>
          {errorMsg}
        </span>
      )}
    </span>
  )
}

// Grants "bring your own Odoo Enterprise": reveals the Enterprise upload
// surface on the user's instances and raises their addon upload cap. It does
// NOT give them Enterprise code — they supply their own, under their own
// licence, into their own instance.
export function EnterpriseToggle({ user }: { user: PlatformUser }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const mut = useMutation({
    mutationFn: (enabled: boolean) => usersApi.updateEnterprise(user.id, enabled),
    onSuccess: () => {
      setErrorMsg(null)
      qc.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (e: unknown) => {
      setErrorMsg(extractApiError(e) ?? t('users.enterpriseToggleFailed', { defaultValue: 'Enterprise update failed' }))
    },
  })
  const on = user.enterprise_enabled
  return (
    <span className="inline-flex items-center gap-2">
      <PillButton
        tone={on ? 'accent' : 'neutral'}
        flagged={Boolean(errorMsg)}
        disabled={mut.isPending}
        title={on
          ? t('users.enterprisePillOnHint', { defaultValue: 'Can upload their own Odoo Enterprise tree. Click to revoke.' })
          : t('users.enterprisePillOffHint', { defaultValue: 'Cannot upload an Enterprise tree. Click to grant.' })}
        ariaLabel={t('users.enterpriseToggleAria', { defaultValue: 'Toggle enterprise access' })}
        onClick={() => { setErrorMsg(null); mut.mutate(!on) }}
      >
        {on ? t('users.enterprisePillOn', { defaultValue: 'Enterprise' }) : t('users.enterprisePillOff', { defaultValue: '-' })}
      </PillButton>
      {errorMsg && (
        <span className="text-[10px] text-amber-700 max-w-[10rem] truncate" title={errorMsg}>
          {errorMsg}
        </span>
      )}
    </span>
  )
}

export function UserRow({ user, expanded, onToggle, onDelete, deleting }: {
  user: PlatformUser
  expanded: boolean
  onToggle: () => void
  onDelete: () => void
  deleting: boolean
}) {
  const { t } = useTranslation()
  const userPlan = (user.plan ?? 'free') as Plan
  const isAdmin = user.role === 'admin'
  const instanceCount = user.instance_count ?? 0

  return (
    <>
      <tr
        className={cx('transition-colors', expanded ? 'bg-brand-50/30' : 'hover:bg-gray-50', !isAdmin && 'cursor-pointer')}
        onClick={() => { if (!isAdmin) onToggle() }}
      >
        <td className="pl-5 pr-3 py-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="shrink-0 w-8 h-8 bg-gradient-to-br from-brand-100 to-brand-50 rounded-full flex items-center justify-center text-brand-600 text-xs font-semibold border border-brand-200">
              {user.email.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-medium text-gray-900 truncate text-sm">{user.email}</span>
                {isAdmin && (
                  <Badge tone="accent" className="uppercase tracking-wide">
                    {t('users.roleAdmin', { defaultValue: 'Admin' })}
                  </Badge>
                )}
              </div>
              {user.company_name && (
                <div className="text-xs text-gray-500 truncate mt-0.5">{user.company_name}</div>
              )}
              <div className="md:hidden text-xs text-gray-500 mt-0.5">
                {user.last_login ? formatTimeAgo(user.last_login, t) : t('users.neverLoggedIn')}
              </div>
            </div>
          </div>
        </td>
        <td className="px-3 py-3">
          <Badge tone={statusTone[user.status] ?? 'neutral'} className="whitespace-nowrap">
            {t(capKey('users.status', user.status))}
          </Badge>
        </td>
        <td className="px-3 py-3">
          <Badge tone={planTone[userPlan]}>{t(capKey('users.plan', userPlan))}</Badge>
        </td>
        <td className="px-3 py-3 text-right text-sm tabular-nums">
          {isAdmin ? (
            <span className="text-gray-400">-</span>
          ) : (
            <span className="text-gray-700">
              {instanceCount} <span className="text-gray-400">/ {user.instance_limit}</span>
            </span>
          )}
        </td>
        <td className="px-3 py-3 hidden md:table-cell">
          <MailToggle user={user} />
        </td>
        <td className="px-3 py-3 hidden lg:table-cell">
          <EnterpriseToggle user={user} />
        </td>
        <td className="px-3 py-3 hidden md:table-cell text-xs text-gray-500" title={user.last_login ? formatDate(user.last_login) : ''}>
          {user.last_login
            ? formatTimeAgo(user.last_login, t)
            : user.status === 'active'
              ? <span className="text-gray-400">{t('users.neverLoggedIn')}</span>
              : <span className="text-gray-400">-</span>}
        </td>
        <td className="px-3 py-3 hidden lg:table-cell text-xs text-gray-500">
          {formatDate(user.created_at)}
        </td>
        <td className="pl-3 pr-5 py-3 text-right">
          {!isAdmin && (
            <LinkButton
              tone="neutral"
              onClick={(e) => { e.stopPropagation(); onToggle() }}
              className="p-1.5 rounded-lg hover:bg-gray-100"
              aria-label={t('common.expand')}
            >
              <svg className={cx('w-4 h-4 transition-transform', expanded && 'rotate-180')} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </LinkButton>
          )}
        </td>
      </tr>

      {expanded && !isAdmin && (
        <tr className="bg-brand-50/20">
          <td colSpan={9} className="px-5 py-5 border-t border-brand-100/50">
            <UserDetailPanel user={user} onDelete={onDelete} deleting={deleting} />
          </td>
        </tr>
      )}
    </>
  )
}
